import { useEffect } from 'react';
import { withMaxWaitLimit } from '../utils';

function debounceEventCreator(cb: () => Promise<unknown>) {
	let isPromiseInProgress = false;

	return async (e: MouseEvent) => {
		const isMainButtonPressed = e.button === 0;
		const isAuxiliaryButtonPressed = e.button === 1;

		if (!isMainButtonPressed && !isAuxiliaryButtonPressed) {
			return Promise.resolve();
		}

		e.preventDefault();
		e.stopImmediatePropagation();

		async function invokePromise() {
			isPromiseInProgress = true;
			await cb();
			isPromiseInProgress = false;
		}

		const promiseFn = isPromiseInProgress ? async () => {} : invokePromise;
		const result = await promiseFn();
		return result;
	};
}

async function triggerOnLinkClick(
	link: HTMLAnchorElement,
	onLinkClick: (url: string) => Promise<unknown>
) {
	const { href, target, rel } = link;

	await withMaxWaitLimit(() => onLinkClick(href));

	if (target !== '') {
		window.open(href, target, rel);
	} else {
		window.location.replace(href);
	}
}

function addClickTrackingOnLinks({
	domNode = new Element(),
	onLinkClick = () => Promise.resolve(null),
}: {
	domNode: Element;
	onLinkClick: (url: string) => Promise<unknown>;
}) {
	const links: HTMLAnchorElement[] = [domNode, ...domNode.querySelectorAll('a')]
		.filter((el) => el.matches('a'))
		.map((e) => e as HTMLAnchorElement);

	const linksWithListener: {
		link: HTMLAnchorElement;
		listener: (ev: MouseEvent) => Promise<void>;
	}[] = links.map((link) => ({
		link,
		listener: debounceEventCreator(() => triggerOnLinkClick(link, onLinkClick)),
	}));

	linksWithListener.forEach((linkWithListener) => {
		linkWithListener.link.addEventListener(
			'click',
			linkWithListener.listener,
			true
		);
		linkWithListener.link.addEventListener(
			'auxclick',
			linkWithListener.listener,
			true
		);
	});

	return linksWithListener;
}

export function useClickTracking(
	ref: { current: HTMLElement | null },
	onLinkClick: (url: string) => Promise<unknown>
) {
	useEffect(() => {
		let linksWithListener: {
			link: HTMLAnchorElement;
			listener: (ev: MouseEvent) => void;
		}[] = [];

		if (ref.current) {
			linksWithListener = addClickTrackingOnLinks({
				domNode: ref.current,
				onLinkClick,
			});
		}

		return () => {
			linksWithListener.forEach(
				(linkWithListener: {
					link: HTMLAnchorElement;
					listener: (ev: MouseEvent) => void;
				}) => {
					linkWithListener.link.removeEventListener(
						'click',
						linkWithListener.listener,
						true
					);
					linkWithListener.link.removeEventListener(
						'auxclick',
						linkWithListener.listener,
						true
					);
				}
			);
		};
	}, [ref.current]);
}
